import { ICustomSurvey, INpsQuestionStats, NumericComparisonOperator, SurveyForm } from '../../extViewmodels';

// LinearScale & NPS both render from `customForm.fields` as a bare `Integer` field; the scale
// bounds/labels + which type it is live on the matching `surveyForm` surveyPart, joined by fieldId.

export const NPS_DEFAULT_START_LABEL = 'Not at all likely';
export const NPS_DEFAULT_END_LABEL = 'Extremely likely';
export const NPS_MIN = 0;
export const NPS_MAX = 10;
export const LINEAR_SCALE_DEFAULT_MIN = 1;
export const LINEAR_SCALE_DEFAULT_MAX = 5;

// NPS is comparable across accounts, so its prompt is not author-editable — computed from the
// survey's company name wherever shown, and normalized onto the stored label at save.
export const npsQuestionPrompt = (companyName?: string | null): string =>
	`How likely are you to recommend ${companyName?.trim() || 'us'} to a friend or colleague?`;

export const clampLinearScaleBounds = (minValue?: number, maxValue?: number): { min: number; max: number } => {
	// Number.isFinite (not typeof) so a NaN bound falls back instead of propagating NaN through the clamp.
	const rawMin = Number.isFinite(minValue) ? (minValue as number) : LINEAR_SCALE_DEFAULT_MIN;
	const rawMax = Number.isFinite(maxValue) ? (maxValue as number) : LINEAR_SCALE_DEFAULT_MAX;
	return { min: rawMin <= 0 ? 0 : 1, max: Math.min(10, Math.max(2, rawMax)) };
};

export const resolveNumericQuestionBounds = (
	questionType: string | undefined,
	minValue?: number,
	maxValue?: number
): { min: number; max: number } | undefined => {
	if (questionType === 'NpsQuestion') return { min: NPS_MIN, max: NPS_MAX };
	if (questionType === 'LinearScaleQuestion') return clampLinearScaleBounds(minValue, maxValue);
	return undefined;
};

type Range = { min: number; max: number };

/** The thresholds at least one answer can satisfy. Only `< min` and `> max` can never fire. */
export const firingRangeForOperator = (range: Range, operator: NumericComparisonOperator): Range => {
	switch (operator) {
		case NumericComparisonOperator.LessThan:
			return { min: range.min + 1, max: range.max };
		case NumericComparisonOperator.GreaterThan:
			return { min: range.min, max: range.max - 1 };
		default:
			return range;
	}
};

const satisfiedByAnyAnswer = (operator: NumericComparisonOperator, value: number, range: Range): boolean => {
	switch (operator) {
		case NumericComparisonOperator.LessThan:
			return range.min < value;
		case NumericComparisonOperator.LessThanOrEqual:
			return range.min <= value;
		case NumericComparisonOperator.GreaterThan:
			return range.max > value;
		case NumericComparisonOperator.GreaterThanOrEqual:
			return range.max >= value;
		case NumericComparisonOperator.Equal:
			return range.min <= value && value <= range.max;
		case NumericComparisonOperator.NotEqual:
			return range.min < range.max || range.min !== value;
		// Fail open: the sweep deletes what this rejects, so an unknown `op` would cost a rule.
		default:
			return true;
	}
};

export const comparisonNeverFires = (operator: NumericComparisonOperator, value: number, range: Range): boolean =>
	!satisfiedByAnyAnswer(operator, value, range);

/** The whole number admitting the same answers as a fractional threshold: `< 6.2` becomes `< 7`. */
export const wholeThresholdEquivalent = (operator: NumericComparisonOperator, value: number): number | null => {
	if (Number.isInteger(value)) return value;
	switch (operator) {
		case NumericComparisonOperator.LessThan:
		case NumericComparisonOperator.GreaterThanOrEqual:
			return Math.ceil(value);
		case NumericComparisonOperator.LessThanOrEqual:
		case NumericComparisonOperator.GreaterThan:
			return Math.floor(value);
		default:
			return null;
	}
};

type ResolvedScaleKind = 'LinearScale' | 'Nps';

export interface ResolvedScaleQuestion {
	kind: ResolvedScaleKind;
	min: number;
	max: number;
	startLabel?: string;
	endLabel?: string;
}

type ScaleQuestionShape = {
	_type?: string;
	fieldId?: string;
	questions?: ScaleQuestionShape[];
	minValue?: number;
	maxValue?: number;
	startLabel?: string | null;
	endLabel?: string | null;
};

const findQuestionByFieldId = (surveyForm: SurveyForm, fieldId: string): ScaleQuestionShape | undefined => {
	for (const part of (surveyForm.surveyParts ?? []) as ScaleQuestionShape[]) {
		if (part._type === 'SurveySection') {
			const nested = (part.questions ?? []).find(q => q.fieldId === fieldId);
			if (nested) return nested;
		} else if (part.fieldId === fieldId) {
			return part;
		}
	}
	return undefined;
};

/** Normalized scale config for a field, or null if it isn't a scale question. NPS is forced to 0–10. */
export const resolveScaleQuestion = (
	surveyForm: SurveyForm | undefined,
	fieldId: string | undefined
): ResolvedScaleQuestion | null => {
	if (!surveyForm || !fieldId) return null;
	const question = findQuestionByFieldId(surveyForm, fieldId);
	if (!question) return null;

	if (question._type === 'NpsQuestion') {
		return {
			kind: 'Nps',
			min: NPS_MIN,
			max: NPS_MAX,
			startLabel: question.startLabel?.trim() || NPS_DEFAULT_START_LABEL,
			endLabel: question.endLabel?.trim() || NPS_DEFAULT_END_LABEL,
		};
	}

	if (question._type === 'LinearScaleQuestion') {
		return {
			kind: 'LinearScale',
			...clampLinearScaleBounds(question.minValue, question.maxValue),
			startLabel: question.startLabel?.trim() || undefined,
			endLabel: question.endLabel?.trim() || undefined,
		};
	}

	return null;
};

// All-time NPS stats read straight off the survey (no extra call); undefined until there are responses.
export const getNpsQuestionStats = (survey: ICustomSurvey | undefined): INpsQuestionStats | undefined =>
	survey?.questionStats?.find((stats): stats is INpsQuestionStats => stats?._type === 'NpsQuestionStats');

export type NpsSegment = 'promoter' | 'passive' | 'detractor';

// Canonical NPS segmentation shared by the reporting views: promoters 9–10, passives 7–8, detractors 0–6.
export const getNpsSegment = (score: number): NpsSegment => {
	if (score >= 9) return 'promoter';
	if (score >= 7) return 'passive';
	return 'detractor';
};

// Descends into V2 SurveySection questions[] as well as top-level parts.
export const findNpsQuestionFieldId = (surveyForm: SurveyForm | undefined): string | undefined => {
	if (!surveyForm) return undefined;
	for (const part of (surveyForm.surveyParts ?? []) as ScaleQuestionShape[]) {
		if (part._type === 'SurveySection') {
			const nested = (part.questions ?? []).find(question => question._type === 'NpsQuestion' && question.fieldId);
			if (nested?.fieldId) return nested.fieldId;
		} else if (part._type === 'NpsQuestion' && part.fieldId) {
			return part.fieldId;
		}
	}
	return undefined;
};
